import React from 'react';
import { Trash2, Lock } from 'lucide-react';
import Tooltip from './Tooltip';

/**
 * Shared key/value grid "table" used by Params, Headers, Cookies, Body form-data,
 * and Environment variables. A CSS grid (see `.req-kv-*` in index.css) whose cells
 * are `min-width: 0`, so long keys/values clip inside their column instead of
 * stretching the row. The delete button only appears on row hover.
 *
 * Rows own their input widgets (key/value differ per table — plain input,
 * SyntaxHighlightInput, HeaderKeyInput, FormDataKeyField, masked field), so those
 * are passed in via `renderKey` / `renderValue`. Everything structural — the grid
 * shell, the enable checkbox, the locked-row lock icon, the hover-reveal delete
 * button, and any extra action buttons — lives here so it is written once.
 *
 * Tab walks the key and value cells only. The checkbox and the delete button are
 * taken out of the tab order by default (owner, 23.09.2026): tabbing down a
 * params table is how you fill it in, and stopping on a checkbox and a bin icon
 * between every field made that three times as many keystrokes. Both are still
 * reachable by mouse, and a caller that wants them focusable can pass an
 * explicit tabIndex.
 */
export function KvTable({ variant, className = '', children }) {
  return (
    <div className={`req-kv-table${variant ? ` req-kv-table--${variant}` : ''}${className ? ` ${className}` : ''}`}>
      {children}
    </div>
  );
}

export function KvRow({
  /** Grid layout variant: 'formdata' (wider key col) | 'env' (wider action col). */
  variant,
  /** Read-only system row (e.g. the User-Agent header): shows a lock, no checkbox/delete. */
  locked = false,
  enabled = true,
  onToggleEnabled,
  checkboxTabIndex = -1,
  /** JSX for the key cell (the appropriate input for this table). */
  renderKey,
  /** JSX for the value cell. */
  renderValue,
  /** Hide the delete button entirely (e.g. the trailing "type to add" row). */
  showRemove = true,
  onRemove,
  removeDisabled = false,
  removeTabIndex = -1,
  /** Extra always-visible action buttons rendered before delete (e.g. secret toggles). */
  actions = null,
  /** Extra class on the row (e.g. secret-variable styling). */
  rowClassName = '',
  /**
   * Two-column row: key and value only, no enable checkbox and no delete.
   *
   * For tables whose rows are a READOUT rather than a list you edit — the
   * variables panel shows what a request resolves to; there is nothing to
   * disable and nothing to remove. `locked` is not the same thing: it keeps
   * both columns and puts a padlock in the first, which says "you may not edit
   * this" about rows that are, in fact, editable.
   */
  bare = false,
}) {
  const cls =
    'req-kv-row' +
    (variant ? ` req-kv-row--${variant}` : '') +
    (bare ? ' req-kv-row--bare' : '') +
    (locked ? ' req-kv-row--locked' : '') +
    (rowClassName ? ` ${rowClassName}` : '');

  if (bare) {
    return (
      <div className={cls}>
        <div className="req-kv-cell req-kv-cell--field">{renderKey}</div>
        <div className="req-kv-cell req-kv-cell--field">{renderValue}</div>
      </div>
    );
  }

  return (
    <div className={cls}>
      <div className="req-kv-cell req-kv-cell--check">
        {locked ? (
          <Lock size={11} />
        ) : (
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => onToggleEnabled?.(e.target.checked)}
            className="checkbox-brand cursor-pointer"
            tabIndex={checkboxTabIndex}
          />
        )}
      </div>
      <div className="req-kv-cell req-kv-cell--field">{renderKey}</div>
      <div className="req-kv-cell req-kv-cell--field">{renderValue}</div>
      <div className="req-kv-cell req-kv-cell--action">
        {actions}
        {!locked && showRemove && (
          <Tooltip content="Remove" side="top">
            <button
              type="button"
              onClick={onRemove}
              disabled={removeDisabled}
              tabIndex={removeTabIndex}
              className="req-kv-delete-btn"
              aria-label="Remove"
            >
              <Trash2 size={14} />
            </button>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

export default KvTable;
